// @architect-approved: HARDCODED_COLORS - Auto-whitelisted for white-label theme system (2026-06-16)

/**
 * DirmLeftMenuPattern — LEFT MENU layout renderer (pattern_id = 5)
 *
 * Left overlay menu with accordion floor sections (inspired by directory-pattern-binder.tsx).
 * Uses same elevator floor layout for accordion structure - no nested tabs, just button navigation.
 * 
 * Layout: Drawer sidebar (left 320px) + main content area (right flex-grow)
 * Accordion sections grouped by floor, buttons navigate like elevator.
 * 
 * Visual design references:
 * - directory-pattern-binder.tsx (accordion structure)
 * - DirmShaftPattern.tsx (manifest loading + floor grouping)
 * 
 * Props: seq (Component_Sequence_Num) → resolves manifest dir → renders left menu
 *
 * Created: 2026-06-21
 */

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import LockIcon from '@mui/icons-material/Lock';
import MenuIcon from '@mui/icons-material/Menu';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { handleManifestButtonClick } from '../../../utils/directoryNavigation';
import { FLOOR_COLORS } from './icon-map';
import { useDirectoryHost } from './useDirectoryHost';

interface DirmLeftMenuPatternProps {
    seq: number;
}

interface MenuButton {
    assignmentId: number;
    floor: string;
    floorNum: number;
    label: string;
    color: string;
    isSubfloor: boolean;
    requiresAuth: boolean;
    visibility_auth_tier: number;
    target_dir_sequence_num: number | null;
    target_directory_name: string | null;
    target_header_text: string | null;
    prefix: string;
    child_sequence_num: number;
    tsx_file_name_target: string;
    directRoute: string | null;
}

const DRAWER_WIDTH = 320;

const DirmLeftMenuPattern: React.FC<DirmLeftMenuPatternProps> = ({ seq }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [drawerOpen, setDrawerOpen] = useState(true);
    const [expanded, setExpanded] = useState<string | false>('02'); // Default to Floor 2

    // Directory resolution (same as DirmShaftPattern)
    // 🔒 LOCKED PATTERN: useDirectoryHost handles all directory resolution
    const target_dir_seq = (location.state as { dirSeq?: number } | null)?.dirSeq;
    const dir = useDirectoryHost(seq);

    // Map manifest buttons → menu buttons, sorted by display_order
    const menuButtons = useMemo(() => {
        if (!dir) return [];
        return dir.buttons
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map(
                (btn): MenuButton => {
                    const isDirectory = btn.target_dir_sequence_num != null;
                    return {
                        assignmentId: btn.assignmentId,
                        floor: String(btn.floorNumber ?? '0').padStart(2, '0'),
                        floorNum: Number(btn.floorNumber) || 0,
                        label: isDirectory
                            ? (btn.target_directory_name || btn.target_header_text || btn.displayLabel)
                            : btn.displayLabel,
                        color: btn.iconColor || FLOOR_COLORS[Number(btn.floorNumber) || 0] || '#607D8B',
                        isSubfloor: !!(btn.subfloorCode && btn.subfloorCode.trim() !== ''),
                        requiresAuth: btn.visibility_auth_tier > 0,
                        visibility_auth_tier: btn.visibility_auth_tier,
                        target_dir_sequence_num: btn.target_dir_sequence_num ?? null,
                        target_directory_name: btn.target_directory_name ?? null,
                        target_header_text: btn.target_header_text ?? null,
                        prefix: btn.prefix,
                        child_sequence_num: btn.child_sequence_num,
                        tsx_file_name_target: btn.tsx_file_name_target,
                        directRoute: btn.directRoute,
                    };
                },
            );
    }, [dir]);

    // Group buttons by floor
    const floorGroups = useMemo(() => {
        const groups: Record<string, MenuButton[]> = {};
        
        for (const btn of menuButtons) {
            if (!groups[btn.floor]) {
                groups[btn.floor] = [];
            }
            groups[btn.floor].push(btn);
        }
        
        return groups;
    }, [menuButtons]);

    const handleFloorChange = (floor: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? floor : false);
    };

    const handleButtonClick = (btn: MenuButton) => {
        const manifestBtn = dir?.buttons.find(b => b.assignmentId === btn.assignmentId);
        if (!manifestBtn) return;
        
        handleManifestButtonClick(manifestBtn, navigate, target_dir_seq || seq);
    };

    if (!dir) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    const floors = Object.keys(floorGroups).sort();

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            {/* Left Drawer (Overlay Menu) */}
            <Drawer
                variant="persistent"
                anchor="left"
                open={drawerOpen}
                sx={{
                    width: DRAWER_WIDTH,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: DRAWER_WIDTH,
                        boxSizing: 'border-box',
                        bgcolor: '#faf8f4',
                        borderRight: '2px solid #e0d8c8',
                    },
                }}
            >
                {/* Menu Header */}
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
                        p: 2,
                        borderBottom: '3px solid #e67e22',
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{ color: 'white', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                        <FolderIcon /> {dir.directoryName || 'Directory Menu'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        {floors.length} Floor{floors.length !== 1 ? 's' : ''} • {menuButtons.length} Button{menuButtons.length !== 1 ? 's' : ''}
                    </Typography>
                </Box>

                {/* Accordion Floor Sections */}
                <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
                    {floors.map((floor) => {
                        const buttons = floorGroups[floor];
                        const floorNum = parseInt(floor, 10);
                        const floorLabel = getFloorLabel(floorNum);
                        const floorColor = FLOOR_COLORS[floorNum] || '#607D8B';

                        return (
                            <Accordion
                                key={floor}
                                expanded={expanded === floor}
                                onChange={handleFloorChange(floor)}
                                disableGutters
                                sx={{
                                    '&:before': { display: 'none' },
                                    borderBottom: '1px solid #e0d8c8',
                                    bgcolor: expanded === floor ? '#fff' : 'transparent',
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon sx={{ color: floorColor }} />}
                                    sx={{
                                        px: 2,
                                        '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 36,
                                                bgcolor: floorColor,
                                                borderRadius: 1,
                                            }}
                                        />
                                        <Box>
                                            <Typography variant="body1" sx={{
                                                fontWeight: 600
                                            }}>
                                                {floorLabel}
                                            </Typography>
                                            <Typography variant="caption" sx={{
                                                color: "text.secondary"
                                            }}>
                                                {buttons.length} button{buttons.length !== 1 ? 's' : ''}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </AccordionSummary>

                                <AccordionDetails sx={{ p: 0 }}>
                                    <List dense disablePadding>
                                        {buttons.map((btn, idx) => (
                                            <React.Fragment key={btn.assignmentId}>
                                                <ListItemButton
                                                    onClick={() => handleButtonClick(btn)}
                                                    sx={{
                                                        pl: 4,
                                                        pr: 2,
                                                        py: 1,
                                                        '&:hover': {
                                                            bgcolor: 'rgba(0,0,0,0.04)',
                                                            borderLeft: `4px solid ${btn.color}`,
                                                        },
                                                        borderLeft: `4px solid transparent`,
                                                        transition: 'all 0.2s ease',
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                                        {btn.requiresAuth ? (
                                                            <LockIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                                                        ) : (
                                                            <ChevronRightIcon sx={{ fontSize: 18, color: btn.color }} />
                                                        )}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={btn.label}
                                                        secondary={btn.isSubfloor ? 'Subfloor' : null}
                                                        slotProps={{
                                                            primary: {
                                                                variant: 'body2',
                                                                sx: { fontWeight: 500 },
                                                            },

                                                            secondary: {
                                                                variant: 'caption',
                                                            }
                                                        }} />
                                                    {btn.requiresAuth && (
                                                        <Chip
                                                            label={`T${btn.visibility_auth_tier}`}
                                                            size="small"
                                                            sx={{
                                                                height: 20,
                                                                fontSize: '0.7rem',
                                                                bgcolor: 'warning.light',
                                                                color: 'warning.dark',
                                                            }}
                                                        />
                                                    )}
                                                </ListItemButton>
                                                {idx < buttons.length - 1 && <Divider sx={{ ml: 4 }} />}
                                            </React.Fragment>
                                        ))}
                                    </List>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Box>

                {/* Footer */}
                <Box
                    sx={{
                        p: 1.5,
                        borderTop: '1px solid #e0d8c8',
                        bgcolor: '#f5f0e8',
                    }}
                >
                    <Typography
                        variant="caption"
                        align="center"
                        sx={{
                            color: "text.secondary",
                            display: "block"
                        }}>
                        Left Menu Pattern • Seq {seq}
                    </Typography>
                </Box>
            </Drawer>

            {/* Main Content Area */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: { sm: `calc(100% - ${drawerOpen ? DRAWER_WIDTH : 0}px)` },
                    transition: 'width 0.3s ease',
                    bgcolor: 'background.default',
                }}
            >
                {/* Toggle Button */}
                <IconButton
                    onClick={() => setDrawerOpen(!drawerOpen)}
                    sx={{
                        position: 'fixed',
                        top: 16,
                        left: drawerOpen ? DRAWER_WIDTH + 16 : 16,
                        zIndex: 1200,
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'primary.dark' },
                        transition: 'left 0.3s ease',
                        boxShadow: 3,
                    }}
                >
                    <MenuIcon />
                </IconButton>

                {/* Main Content */}
                <Paper
                    sx={{
                        p: 4,
                        minHeight: 'calc(100vh - 48px)',
                        mt: 6,
                    }}
                >
                    <Typography variant="h4" gutterBottom>
                        {dir.directoryName || 'Directory'}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: "text.secondary",
                            marginBottom: "16px"
                        }}>
                        Select an option from the left menu to navigate.
                    </Typography>

                    {/* Directory Stats */}
                    <Box sx={{ mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip
                            label={`${floors.length} Floors`}
                            color="primary"
                            variant="outlined"
                        />
                        <Chip
                            label={`${menuButtons.length} Total Buttons`}
                            color="secondary"
                            variant="outlined"
                        />
                        <Chip
                            label={`Seq ${target_dir_seq || seq}`}
                            variant="outlined"
                        />
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

/**
 * Get floor label from floor number
 */
function getFloorLabel(floorNum: number): string {
    if (floorNum === 0) return 'Ground Floor';
    if (floorNum === 2) return 'Floor 2 - Collaboration';
    if (floorNum === 3) return 'Floor 3 - Tenant Offices';
    if (floorNum === 4) return 'Floor 4 - Platform Tools';
    return `Floor ${floorNum}`;
}

export default DirmLeftMenuPattern;
